import React, { useState, useEffect } from 'react'
import Modal from 'react-modal'
import { useParams, useNavigate } from 'react-router-dom'
import { YACHT_DATA } from '../data/yachts'
import heroImage from '../assets/images/focus-yacht-header.jpeg'
import { motion } from 'framer-motion'
import ExpandButton from '../components/shared/ExpandButton'

Modal.setAppElement('#root')

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
}

const section2Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.2, ease: 'easeOut' },
  },
}

const imageVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: 'easeOut' },
  }),
}

const FocusYachtPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [yacht, setYacht] = useState(null)
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(null)

  useEffect(() => {
    const yachtData = YACHT_DATA.find((y) => y.id === parseInt(id, 10))
    setYacht(yachtData)
  }, [id])

  const openModal = (index) => {
    setSelectedImageIndex(index)
    setModalIsOpen(true)
  }

  const closeModal = () => {
    setSelectedImageIndex(null)
    setModalIsOpen(false)
  }

  const handleNext = () => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === yacht.previewImages.length - 1 ? 0 : prevIndex + 1
    )
  }

  const handlePrev = () => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === 0 ? yacht.previewImages.length - 1 : prevIndex - 1
    )
  }

  if (!yacht) {
    return <p>Loading yacht data...</p>
  }

  return (
    <div className='flex flex-col min-h-screen mb-20'>
      {/* Hero Section */}
      <div
        className='relative bg-cover bg-center h-[500px]'
        style={{
          backgroundImage: `linear-gradient(rgba(23, 37, 84, .4), rgba(23, 37, 84, .4)),url(${heroImage})`,
        }}
      >
        <div className='flex flex-col items-center justify-center h-full text-center text-white w-full'>
          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.25 }}
            className='text-4xl font-light mb-2'
          >
            {yacht.title}
          </motion.h1>
          <motion.div
            initial={{ width: '0%' }}
            whileInView={{ width: '200px' }}
            transition={{ duration: 0.75, ease: 'easeOut', delay: 0.5 }}
            className='h-[2px] bg-white'
          />
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.6 }}
            className='text-lg mt-4'
          >
            {yacht.tabName}
          </motion.p>
        </div>
      </div>

      <main className='flex-grow max-w-md md:max-w-5xl mx-auto pb-8 pt-12'>
        {/* Back Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 5 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.7 }}
          onClick={() => navigate('/yachts')}
          className='flex items-center space-x-2 text-brandPrimary/75 hover:text-brandPrimary ease duration-500 my-4'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            strokeWidth={2}
            stroke='currentColor'
            className='w-5 h-5'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M15.75 19.5L8.25 12l7.5-7.5'
            />
          </svg>
          <span>Back to Yachts</span>
        </motion.button>

        {/* Details Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className='grid grid-cols-1 md:grid-cols-2 gap-4 py-8'
        >
          <motion.img
            src={yacht.previewImage}
            alt={yacht.title}
            className='w-full h-auto rounded-lg'
          />
          <div className='flex flex-col space-y-4 mt-8 md:mt-4 md:ml-4'>
            <div className='flex text-brandPrimary'>
              <span className='mr-2 font-semibold'>Length:</span>
              <span>{yacht?.boatData?.length}m</span>
            </div>
            <div className='flex text-brandPrimary'>
              <span className='mr-2 font-semibold'>Berths:</span>
              <span>{yacht?.boatData?.berths}</span>
            </div>
            <div className='flex text-brandPrimary'>
              <span className='mr-2 font-semibold'>Cabins:</span>
              <span>{yacht?.boatData?.cabins}</span>
            </div>
            <div className='flex text-brandPrimary'>
              <span className='mr-2 font-semibold'>WC:</span>
              <span>{yacht?.boatData?.wc}</span>
            </div>
            <div className='flex text-brandPrimary'>
              <span className='mr-2 font-semibold'>Cruising Speed:</span>
              <span>{yacht?.boatData?.cruisingSpeed} KNOTS</span>
            </div>
          </div>
        </motion.section>
        {/* CTA Section */}
        <motion.section
          variants={sectionVariants}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          className='py-8 flex justify-center'
        >
          <ExpandButton
            buttonText='Make A Request'
            onClick={() =>
              navigate('/make-request', {
                state: { yachtName: yacht.title },
              })
            }
          />
        </motion.section>

        {/* Paragraph Section */}
        <motion.section
          variants={section2Variants}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          className='py-8'
        >
          <p className='tracking-wide leading-relaxed text-gray-600'>
            {yacht.introParagraph}
          </p>
        </motion.section>

        {/* Image Grid Section */}
        <motion.section
          variants={sectionVariants}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          className='grid grid-cols-2 md:grid-cols-4 gap-4 pt-8'
        >
          {yacht.previewImages.map((image, index) => (
            <motion.img
              key={index}
              src={image}
              alt={`Preview ${index + 1}`}
              className='w-full h-[182px] cursor-pointer rounded-lg hover:shadow-lg transition-shadow'
              variants={imageVariants}
              custom={index}
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true }}
              onClick={() => openModal(index)}
            />
          ))}
        </motion.section>
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className='grid grid-cols-1 md:grid-cols-2 gap-4 pt-24'
        >
          {/* Included Section */}
          <div className='mb-8'>
            <h3 className='text-xl font-semibold text-gray-600 mb-4'>
              INCLUDED IN PRICE:
            </h3>
            <ul className='list-disc list-inside text-gray-700 space-y-2'>
              <li>12% VAT (Greece)</li>
              <li>Snorkelling equipment</li>
              <li>Complete set of professional fishing equipment.</li>
              <li>Coffee machine</li>
              <li>Ice maker</li>
              <li>Bed Linen and pillows</li>
              <li>Standard yacht equipment.</li>
            </ul>
          </div>

          {/* Not Included Section */}
          <div>
            <h3 className='text-xl font-semibold text-gray-600 mb-4'>
              NOT INCLUDED IN PRICE:
            </h3>
            <ul className='list-disc list-inside text-gray-700 space-y-2'>
              <li>Fuel</li>
              <li>Water</li>
              <li>Additional water toys (Yamaha sea scooters, SUP)</li>
              <li>
                Electric Scooters, Electric Bikes, Drone (if requested,
                additional charges will apply)
              </li>
              <li>Wifi internet up to 20 GB per week</li>
              <li>End cleaning</li>
              <li>Provisions</li>
              <li>
                Crew which is charged as follows:
                <ul className='list-disc list-inside pl-6'>
                  <li>Captain: €200/day</li>
                  <li>Chef: €170/day (if requested)</li>
                  <li>Hostess: €150/day (if requested)</li>
                </ul>
              </li>
            </ul>
          </div>
        </motion.section>
      </main>

      {/* Modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={true} // Allows click-away to close the modal
        contentLabel='Image Preview'
        className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 px-4 md:px-0'
        overlayClassName='fixed inset-0 bg-black bg-opacity-50'
      >
        {selectedImageIndex !== null && (
          <div className='bg-white p-4 rounded-lg max-w-2xl mx-auto relative'>
            <div className='mb-4 flex justify-end'>
              {/* Close Button */}
              <button
                onClick={closeModal}
                className='ml-auto text-gray-500 hover:text-gray-800 ease duration-500'
              >
                ✕
              </button>
            </div>

            {/* Image */}
            <img
              src={yacht.previewImages[selectedImageIndex]}
              alt={`Selected ${selectedImageIndex + 1}`}
              className='w-full h-auto'
            />
            {/* Navigation Buttons */}
            <div className='flex justify-between mt-4'>
              <button
                onClick={handlePrev}
                className='px-4 py-2 text-gray-500 hover:text-gray-800 ease duration-500  rounded bg-white hover:bg-white'
              >
                Previous
              </button>
              <button
                onClick={handleNext}
                className='px-4 py-2 text-gray-500 hover:text-gray-800 ease duration-500  rounded bg-white hover:bg-white'
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default FocusYachtPage
